import { customLog } from 'helpers/customLog';
import { T_Coupon } from 'stores/coupon.store';

import { productItem } from 'types/order.type';

import {
  ADDITIONAL_SKUS_LIST,
  BUNDLE,
  CATEGORY,
  DISCOUNT_SKUS_LIST,
  HARDWARE,
  PACKAGES,
  PACKAGES_LIST,
  PRODUCT_SKU_PACKAGE_NAME,
  VOUCHER_PRODUCT_SKUS_LIST,
  productSkuIds,
} from '@/lib/constants';
import { removeDuplicateFromArrayList, uniqueId } from '@/lib/utils';
import { sentryService } from '@/services/index';

import { productCartPayload } from './cartHelpers';
import { getPackagesBasedOnCategory } from './productFilteredBasedOnCategory';
import { extractCtPrices } from './useCustomerOrder';

type Product = {
  id: number;
  kkProductInfo?: {
    sku?: number | string;
  };
  [key: string]: any;
};

type OrderProduct = {
  productId: number;
  [key: string]: any;
};

export const createPayLoadForProduct = (productItem: any, kkProductItem: any, sku: number) => {
  return {
    ...productItem.fields,
    id: kkProductItem.id,
    kkProductInfo: {
      price: kkProductItem.price0,
      priceIncTax: kkProductItem.priceIncTax,
      priceExTax: kkProductItem.priceExTax,
      quantity: kkProductItem.quantity,
      id: kkProductItem.id,
      sku: sku,
      categoryId: kkProductItem.categoryId,
      actualPrice: kkProductItem.price,
      name: kkProductItem.name,
      image: kkProductItem.image,
      custom9: kkProductItem.custom9,
    },
  };
};

export const mapGraphQLProducts = (kkProducts: any[], productData: any[]) => {
  const lists: any[] = [];
  const skuLists: any[] = [];
  customLog('productData', productData);
  productData.forEach((it: any) => {
    const productSku: string[] = it.fields.productSku || [];
    skuLists.push([...productSku]);
  });
  const uniqueSkus = removeDuplicateFromArrayList(skuLists);

  if (uniqueSkus.length) {
    uniqueSkus.forEach((sku: any) => {
      productData.some((item: any) => {
        const skuString = item?.fields?.productSku?.join(',') || '';
        if (
          skuString === sku &&
          item?.fields?.categories !== BUNDLE &&
          item?.fields?.type !== BUNDLE
        ) {
          const kkProductItem = kkProducts.find((kkItem: any) => kkItem.sku === sku);
          if (kkProductItem) {
            lists.push({
              ...item.fields,
              id: kkProductItem.id,
              // KK products expose a categories array; CT products expose a flat categoryName string
              variant:
                kkProductItem?.categoryName ||
                (kkProductItem?.categories && kkProductItem?.categories[0]?.name) ||
                '',
              kkProductInfo: {
                price: kkProductItem.price0,
                price1: kkProductItem.price1,
                priceIncTax: kkProductItem.priceIncTax,
                priceExTax: kkProductItem.priceExTax,
                quantity: kkProductItem.quantity,
                id: kkProductItem.id,
                sku: sku,
                categoryId: kkProductItem.categoryId,
                actualPrice: kkProductItem.price,
                name: kkProductItem.name,
                image: kkProductItem.image,
                custom9: kkProductItem.custom9,
                custom1: kkProductItem.custom1,
                model: kkProductItem.model,
              },
            });
          }

          return true;
        }

        return false;
      });
    });
  }

  let SKUS_LIST = [...ADDITIONAL_SKUS_LIST, ...DISCOUNT_SKUS_LIST, ...VOUCHER_PRODUCT_SKUS_LIST];
  if (SKUS_LIST.length > 0) {
    SKUS_LIST.forEach((sku: any) => {
      const kkProductItem = kkProducts.find((kkItem: any) => kkItem.sku === sku);
      if (kkProductItem) {
        lists.push({
          id: kkProductItem.id,
          title: kkProductItem.name,
          category: kkProductItem.categoryName,
          categories: kkProductItem.categoryName,
          subCategory: null,
          type: null,
          titleImageUrl: null,
          sku: sku,
          kkProductInfo: {
            price: kkProductItem.price0,
            price1: kkProductItem.price1,
            priceIncTax: kkProductItem.priceIncTax,
            priceExTax: kkProductItem.priceExTax,
            quantity: kkProductItem.quantity,
            id: kkProductItem.id,
            sku: sku,
            categoryId: kkProductItem.categoryId,
            actualPrice: kkProductItem.price,
            name: kkProductItem.name,
            image: kkProductItem.image,
            custom9: kkProductItem.custom9,
            custom1: kkProductItem.custom1,
            model: kkProductItem.model,
          },
        });
      }
    });
  }

  return lists;
};

export const freeMappingProduct = (
  productList: any,
  cartProducts: any,
  freeMappingProductSkuIds: any[],
  category: string | undefined
) => {
  const productMap: { [key: string]: number } = {};

  productList.forEach((product: any) => {
    if (!product.categories || !product.kkProductInfo?.sku) return;
    if (product.categories.toLocaleLowerCase() === category) {
      productMap[product.kkProductInfo.sku] = product.id;
    }
  });

  const cartExistingProductIds = cartProducts.reduce(
    (acc: { [key: number]: boolean }, item: any) => {
      acc[item.productId] = true;
      return acc;
    },
    {}
  );

  const freeBundleProductBasedOnSkuIds = freeMappingProductSkuIds;

  for (let sku of freeBundleProductBasedOnSkuIds) {
    const productId = productMap[sku];
    if (!productId || !cartExistingProductIds[productId]) {
      return false;
    }
  }

  return true;
};

export const addFreeProduct = async (productList: any) => {
  let product: any | null;
  try {
    product = productList.find(
      (item: any) => item.kkProductInfo.sku === productSkuIds?.movies?.primary
    );
  } catch (error) {
    sentryService.captureException(error);
  }
  return {
    product,
  };
};

export const getDeviceSelectionBasedOnSkuId = (productList: any) => {
  const boxSkuIds: number[] = productSkuIdList(PRODUCT_SKU_PACKAGE_NAME.SKY_BOX) || [];
  const podSkuIds: number[] = productSkuIdList(PRODUCT_SKU_PACKAGE_NAME.SKY_POD) || [];

  const boxSkuSet = new Set(boxSkuIds.map(Number));
  const podSkuSet = new Set(podSkuIds.map(Number));

  for (const item of productList) {
    const sku = Number(item?.kkProductInfo?.sku);
    if (boxSkuSet.has(sku)) {
      return boxSkuSet;
    }
    if (podSkuSet.has(sku)) {
      return podSkuSet;
    }
  }
  return null;
};

export const cartOrderProduct = (
  orderProduct: OrderProduct[],
  productList: Product[],
  category?: string
): any[] => {
  if (!orderProduct.length || !productList.length) return [];

  const cartOrderProduct: any[] = [];

  const productMap = new Map<number, Product>(productList.map(p => [p.id, p]));

  const addedProductIds = new Set<number>();
  const mappingOrderProduct: Product[] = [];

  for (const { productId } of orderProduct) {
    if (!addedProductIds.has(productId)) {
      const productItem = productMap.get(productId);
      if (productItem) {
        mappingOrderProduct.push(productItem);
        addedProductIds.add(productId);
      }
    }
  }

  if (!mappingOrderProduct.length) return [];

  const resolvedCategory = (() => {
    if (category) return category;
    const cats = new Set(
      mappingOrderProduct.map(p => (p as any).categories?.toLowerCase()).filter(Boolean)
    );
    const hasTV = cats.has(CATEGORY.TV.toLowerCase());
    const hasBB = cats.has(CATEGORY.BROADBAND.toLowerCase());
    if (hasTV && hasBB) return CATEGORY.TV_BROADBAND;
    if (hasBB) return CATEGORY.BROADBAND;
    return CATEGORY.TV;
  })();

  const skuIdSet = getDeviceSelectionBasedOnSkuId(mappingOrderProduct);
  const occurrenceNumber = uniqueId();

  const updateProductList = mappingOrderProduct.map(item => {
    const sku = Number(item?.kkProductInfo?.sku);
    return {
      ...item,
      occurrenceNumber: skuIdSet && skuIdSet.has(sku) ? occurrenceNumber : null,
    };
  });

  for (const product of updateProductList) {
    const cartProductItem = productCartPayload(product, product.occurrenceNumber, resolvedCategory);
    if (cartProductItem) {
      cartOrderProduct.push(cartProductItem);
    }
  }

  customLog('cartOrderProduct', cartOrderProduct);
  return cartOrderProduct;
};

export const mapCampaignsToCoupons = (campaigns: any[]): T_Coupon[] => {
  if (!campaigns?.length) return [];
  return campaigns.map(c => ({
    couponCode: c.campaign ?? '',
    name: c.name ?? '',
    description: c.description ?? '',
    id: c.id ?? '',
    custom1: c.custom1 ?? '',
    custom2: c.custom2 ?? '',
    custom3: c.custom3 ?? '',
    custom4: c.custom4 ?? '',
    custom5: c.custom5 ?? '',
  }));
};

export const productMappingBasedOnCustomerId = (
  response: any,
  productList: any,
  category?: string
) => {
  // CommerceTools: response has { items, ongoingTotalPrice, cartTotalPrice, campaigns }
  const cartProductList = cartOrderProduct(response.items, productList, category);
  const { subTotalPrice, otTotalNoOtcPrice, totalPrice } = extractCtPrices(response);
  return {
    subTotalPrice,
    totalPrice,
    otTotalNoOtcPrice,
    orderProducts: cartProductList,
    // Raw CT items with discountPrice/price fields — used by setPrice for accurate pricing.
    // cartProductList uses KK catalog pricing and lacks discountPrice.
    pricingItems: response.items ?? [],
    campaigns: response.campaigns ?? [],
  };
};

export const getProductSkuIdListBasedOnCategory = async (
  category: string | undefined,
  categoryType: string
): Promise<number[]> => {
  if (category) {
    switch (category) {
      case CATEGORY.TV.toLocaleLowerCase():
        return productSkuIdList(PRODUCT_SKU_PACKAGE_NAME.SKY_BUNDLES);
      case CATEGORY.BROADBAND.toLocaleLowerCase():
        return [];
      case CATEGORY.TV_BROADBAND.toLocaleLowerCase():
        if (categoryType.toLocaleLowerCase() === CATEGORY.TV.toLocaleLowerCase()) {
          return productSkuIdList(PRODUCT_SKU_PACKAGE_NAME.SKY_BUNDLES);
        } else return [];
    }
  }
  return [];
};

export const productSkuIdList = (name: string) => {
  let skuId: number[] = [];

  const key = name as keyof typeof productSkuIds;

  if (productSkuIds[key]?.primary) {
    skuId = productSkuIds[key].primary.split(',').map(Number);
  }
  return skuId;
};

export const getDeviceType = (selectedDeviceId: string) => {
  const skyBoxIds = productSkuIds?.skyBox?.primary?.split(',');
  const skyPodIds = productSkuIds?.skyPod?.primary?.split(',');
  const selectedIds = selectedDeviceId?.split('-');

  if (skyBoxIds.includes(selectedIds?.[0])) {
    //COMPARING ONLY FIRST DEVICE VALUES
    return PRODUCT_SKU_PACKAGE_NAME.SKY_BOX;
  }
  if (skyPodIds.includes(selectedIds?.[0])) {
    //COMPARING ONLY FIRST DEVICE VALUES
    return PRODUCT_SKU_PACKAGE_NAME.SKY_POD;
  }
};

/** this function returns two functions to know if pod or box devices exist in given Products List  */
export const hasDeviceType = (products: productItem[]) => {
  const { selectedTVDeviceIDs, selectedBBDeviceIDs } = products.reduce(
    (acc, product) => {
      if (product?.type !== HARDWARE) return acc;

      const category = product?.category?.toLocaleLowerCase();
      if (category === CATEGORY.TV.toLocaleLowerCase()) {
        acc.selectedTVDeviceIDs.push(product.sku);
      }

      if (category === CATEGORY.BROADBAND.toLocaleLowerCase()) {
        acc.selectedBBDeviceIDs.push(product.sku);
      }

      return acc;
    },
    {
      selectedTVDeviceIDs: [] as string[],
      selectedBBDeviceIDs: [] as string[],
    }
  );

  const skyBoxIds = productSkuIds.skyBox.primary.split(',');
  const skyPodIds = productSkuIds.skyPod.primary.split(',');
  const selectedTVDeviceIDSet = new Set(selectedTVDeviceIDs);

  const skyBox = () => {
    return skyBoxIds.some((id: string) => selectedTVDeviceIDSet.has(id));
  };
  const skyPod = () => {
    return skyPodIds.some((id: string) => selectedTVDeviceIDSet.has(id));
  };
  const bbDevice = () => selectedBBDeviceIDs.length > 0;

  return { skyBox, skyPod, bbDevice };
};

export const isAddonsProductExistsOrNot = (productList: any, product: any) => {
  const isAdded = (id: any) => {
    return product.some((it: any) => it.productId === id);
  };
  const filteredAddons =
    productList.length > 0 &&
    productList.filter(
      (product: any) =>
        product?.type?.toLowerCase() === PACKAGES.toLowerCase() &&
        [CATEGORY.TV.toLowerCase()].includes(product?.categories.toLowerCase()) &&
        !isAdded(product.id)
    );

  return filteredAddons.length > 0;
};

export const checkValidationForPlanSelection = async (category: string, products: any) => {
  const normalizedCategory = category.toLowerCase();
  const isTV = normalizedCategory === CATEGORY.TV.toLowerCase();
  const isBroadband = normalizedCategory === CATEGORY.BROADBAND.toLowerCase();
  const isTVBroadband = normalizedCategory === CATEGORY.TV_BROADBAND.toLowerCase();

  let planLen = 0;
  let deviceLen = 0;
  const tvPlanLen = products.filter((item: any) => item.occurrenceNumber === null).length;
  const tvDeviceLen = products.filter((item: any) => item.occurrenceNumber !== null).length;
  const isBoosterPlanExists = products?.some(
    (it: Product) =>
      it.type === PACKAGES &&
      it.subCategory.toLowerCase() === PACKAGES_LIST.BB_UPGRADE.toLowerCase() &&
      it.category.toLowerCase() === category.toLowerCase()
  );
  if (isTV) {
    planLen = tvPlanLen;
    deviceLen = tvDeviceLen;
  } else if (isBroadband) {
    const filteredLists = products?.filter(
      (it: any) =>
        it.type === HARDWARE && it.category.toLocaleLowerCase() === category.toLocaleLowerCase()
    );
    const planLists = getPackagesBasedOnCategory(products, category);
    deviceLen = isBoosterPlanExists ? filteredLists.length : 1;
    planLen = planLists.length;
  } else if (isTVBroadband) {
    const filteredLists = products?.filter(
      (it: any) =>
        it.type === HARDWARE &&
        it.category.toLocaleLowerCase() === CATEGORY.BROADBAND.toLocaleLowerCase()
    );
    const planLists = getPackagesBasedOnCategory(products, category);
    const hasDevice =
      tvDeviceLen > 0 && isBoosterPlanExists ? filteredLists.length > 0 : tvDeviceLen;
    const hasPlan = tvPlanLen > 0 && planLists.length > 0;

    deviceLen = hasDevice ? 1 : 0;
    planLen = hasPlan ? 1 : 0;
  }
  return {
    planLen,
    deviceLen,
  };
};

export const createProductInfoModalData = (productItem: any, category: string) => {
  const isTVBB = category && category.toLowerCase() === CATEGORY.TV_BROADBAND.toLowerCase();
  const updatePrice =
    isTVBB && productItem.categories.toLowerCase() === CATEGORY.BROADBAND.toLowerCase()
      ? productItem.kkProductInfo.price1
      : productItem.kkProductInfo.price;
  return {
    categories: productItem.categories ?? category,
    termsAndCondition: productItem.teamCondition,
    thumbnail: productItem.titleImageDesktop?.fields?.file,
    title: productItem.title,
    channelSet: productItem.channelSet,
    cardThumbnail: productItem.smallThumbnail?.fields?.file,
    productPrice: updatePrice,
    discountedPrice: 0,
    lozengeText: productItem.lozengeText,
    infoList: productItem.infoList,
    bulletImage: productItem.bulletImage,
    productInfo: productItem.productInfo,
    legalLine: productItem.legalLine,
    termsAndConditionsCaption: productItem.termsAndConditionsCaption,
  };
};
